import type { CityConfig } from "../city-template/types";

export const glendaleConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Glendale",
  state: "California",
  stateAbbr: "CA",
  slug: "glendale",
  route: "/cities/glendale",
  title: "Emergency Passport Glendale, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Glendale, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Fernando Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Glendale, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Glendale and San Fernando Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Glendale%20California%20cityscape%20at%20golden%20hour%20with%20modern%20skyline%2C%20Brand%20Boulevard%20shopping%20district%2C%20Americana%20at%20Brand%20visible%2C%20San%20Gabriel%20Mountains%20in%20the%20background%2C%20warm%20sunset%20light%20reflecting%20off%20modern%20buildings%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20modern%20suburban%20urban%20environment&width=1800&height=700&seq=glendale-hero-001&orientation=landscape",
  heroImageAlt: "Glendale California skyline at golden hour with modern buildings and San Gabriel Mountains in the background",
  geoPosition: "34.1425;-118.2551",
  geoPlacename: "Glendale, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 25–35 minutes from Glendale via I-5",
  },
  nearbyCitiesZips: [
    { name: "Glendale", zips: "91201–91210" },
    { name: "Burbank", zips: "91501–91506, 91510" },
    { name: "La Cañada", zips: "91011, 91012" },
    { name: "La Crescenta", zips: "91214" },
    { name: "Pasadena", zips: "91101–91105" },
    { name: "Eagle Rock", zips: "90041, 90042" },
    { name: "Los Feliz", zips: "90027, 90039" },
    { name: "Atwater Village", zips: "90039" },
    { name: "Silver Lake", zips: "90026, 90039" },
    { name: "Studio City", zips: "91604, 91614" },
    { name: "North Hollywood", zips: "91601, 91602, 91606" },
    { name: "Toluca Lake", zips: "91602" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent San Fernando Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Los Angeles", route: "/cities/los-angeles" },
    { name: "Fast passport renewal in Burbank", route: "/cities/burbank" },
    { name: "Emergency passport help in Pasadena", route: "/cities/pasadena" },
    { name: "Urgent passport services in Santa Monica", route: "/cities/santa-monica" },
    { name: "Same-day passport help in Long Beach", route: "/cities/long-beach" },
    { name: "Rush passport services in Irvine", route: "/cities/irvine" },
    { name: "Passport courier services in Anaheim", route: "/cities/anaheim" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Glendale remotely via secure courier. We do not have a walk-in office in Glendale — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for San Fernando Valley travelers.",
  fpcOfficeAddress: "225 S Lake Ave, Pasadena, CA 91101",
  fpcOfficeDistance: "Serving all of San Fernando Valley",
  serviceAreaIntro: "From the Americana at Brand to Montrose, from the Glendale Galleria to the Verdugo Hills — Fast Passport Center serves the entire Glendale community. We regularly help travelers from Burbank, La Cañada, Pasadena, and Eagle Rock get their passports fast.",
  nearbyCitiesIntro: "We serve all Glendale neighborhoods and nearby San Fernando Valley and foothill cities including Burbank, La Cañada, La Crescenta, Pasadena, Eagle Rock, and Los Feliz. If you are in the San Fernando Valley or nearby LA neighborhoods, we have you covered.",
  howItWorksIntro: "Most Glendale travelers complete everything online and ship documents overnight. No need to drive downtown or wait in line at the regional agency.",
  mistakesIntro: "With Burbank Airport nearby and LAX just 25 miles away, Glendale travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Glendale and San Fernando Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Glendale? Here are the most common ones we hear from San Fernando Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent San Fernando Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michael R.",
      location: "Glendale, CA",
      rating: 5,
      text: "My flight to Seoul was in 2 days and my passport was expired. Fast Passport Center got me a new one in 28 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "Angela T.",
      location: "Burbank, CA",
      rating: 5,
      text: "Needed to renew for a family vacation to Hawaii. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Robert C.",
      location: "La Cañada, CA",
      rating: 5,
      text: "Lost my passport the week before my anniversary trip to Paris. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Nina S.",
      location: "Pasadena, CA",
      rating: 5,
      text: "As a frequent international traveler from the San Fernando Valley, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "gl-1",
      name: "Glendale Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "313 E Broadway",
      city: "Glendale",
      state: "CA",
      zip: "91205",
      phone: "(818) 242-1731",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Glendale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "gl-2",
      name: "Burbank Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2230 N Hollywood Way",
      city: "Burbank",
      state: "CA",
      zip: "91505",
      phone: "(818) 842-6141",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.5 miles from Glendale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "gl-3",
      name: "Pasadena City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "100 N Garfield Ave",
      city: "Pasadena",
      state: "CA",
      zip: "91101",
      phone: "(626) 744-4121",
      hours: "Mon–Thu: 7:30 AM – 5:30 PM, Fri: 8:00 AM – 5:00 PM",
      distance: "6 miles from Glendale",
      website: "https://www.cityofpasadena.net/city-clerk/",
      websiteLabel: "Pasadena City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "gl-4",
      name: "Eagle Rock Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1520 Colorado Blvd",
      city: "Los Angeles",
      state: "CA",
      zip: "90041",
      phone: "(323) 254-2931",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4 miles from Glendale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for nearby LA residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "gl-5",
      name: "Studio City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "12037 Ventura Blvd",
      city: "Studio City",
      state: "CA",
      zip: "91604",
      phone: "(818) 985-1973",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles from Glendale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Valley residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "gl-6",
      name: "LA County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "12400 Imperial Hwy",
      city: "Norwalk",
      state: "CA",
      zip: "90650",
      phone: "(562) 462-2133",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "25 miles from Glendale",
      website: "https://www.lavote.net/",
      websiteLabel: "LA County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Armenian Community Travel", agency: "No community-specific expertise", fpc: "Armenian-American diaspora and international family travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "San Fernando Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Fernando Valley acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Valley applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Valley travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Glendale." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Glendale address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Glendale before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Glendale for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most San Fernando Valley travelers." },
    { q: "How fast can I renew my passport in Glendale?", a: "Fast Passport Center offers fast passport renewal in Glendale in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Glendale in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Glendale?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Glendale residents." },
    { q: "Can I expedite a child passport in Glendale?", a: "Yes. Fast Passport Center expedites child passports in Glendale for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk San Fernando Valley families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Glendale with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Glendale?",
      a: "With Fast Passport Center's emergency courier service, Glendale travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Los Angeles Passport Agency downtown?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Glendale?",
      a: "Popular options include the Glendale Main Post Office (91205), Burbank Main Post Office (91505), Pasadena City Clerk (91101), and Eagle Rock Post Office (90041). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Glendale?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in the San Fernando Valley or nearby foothills?",
      a: "We serve all San Fernando Valley and foothill communities including Burbank, La Cañada, La Crescenta, Pasadena, Eagle Rock, and Los Feliz. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Glendale?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Glendale?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Glendale and Burbank offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Glendale?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Glendale?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk San Fernando Valley families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Glendale", address: "1245 S Glendale Ave, Glendale, CA 91205", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Burbank", address: "1011 N San Fernando Blvd, Burbank, CA 91504", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "FedEx Office — Glendale", address: "205 N Brand Blvd, Glendale, CA 91203", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.office.fedex.com/" },
    { name: "UPS Store — Pasadena", address: "150 E Holly St, Pasadena, CA 91103", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Burbank", address: "1301 N Victory Blvd, Burbank, CA 91502", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Rite Aid — Eagle Rock", address: "4126 Eagle Rock Blvd, Los Angeles, CA 90065", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "Postal Annex — Glendale", address: "222 N Brand Blvd, Glendale, CA 91203", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/" },
    { name: "Costco — Alhambra", address: "2207 W Commonwealth Ave, Alhambra, CA 91803", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/" },
  ],
  dmvLocations: [
    {
      name: "DMV — Glendale",
      address: "1335 W Glenoaks Blvd, Glendale, CA 91201",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Burbank",
      address: "1441 W Olive Ave, Burbank, CA 91506",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Pasadena",
      address: "49 S Rosemead Blvd, Pasadena, CA 91107",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Hollywood",
      address: "8033 Sunset Blvd, Los Angeles, CA 90046",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Hollywood and nearby Valley residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "8 miles from Glendale",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "The closest airport for Glendale travelers. Domestic flights and some international connections. Much easier access than LAX.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "25 miles from Glendale",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-5 and I-405.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "LGB",
      name: "Long Beach Airport",
      distance: "35 miles from Glendale",
      address: "4100 Donald Douglas Dr, Long Beach, CA 90808",
      note: "Convenient for domestic flights. Less crowded than LAX. Good for South Bay and coastal destinations.",
      flightUrl: "https://www.longbeach.gov/lgb/",
      flightLabel: "Long Beach Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for Los Angeles County residents.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters in Los Angeles County.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Los Angeles County Registrar-Recorder/County Clerk. Certified copies available for name changes and legal purposes in Glendale and the San Fernando Valley.",
      url: "https://www.lavote.gov/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Los Angeles County Superior Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.lacourt.org/",
    },
  ],
};